import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { ModifyReferenceAndNotesDialogBody } from "../Dialogs/Internal/ModifyReferenceAndNotesDialogBody";

interface ReferenceNumberAndNotes {
  referenceNumber: string;
  // Only set for Amazon warehouse deliveries; the server packs them into the
  // carrier-facing reference
  amazonReferenceId: string | undefined;
  amazonShipmentId: string | undefined;
  boothNumber: string;
  notes: string;
}

interface ShowArgs {
  shipmentId: string;
  referenceNumber: string;
  amazonReferenceId: string | undefined;
  amazonShipmentId: string | undefined;
  boothNumber: string;
  notes: string;
  distributionWarehouseBrand?: string | undefined;
}

// Hosts the legacy reference-and-notes form (it renders its own title and
// buttons) in a ui-core Dialog, bridged back to the awaited result.
export function useViewShipmentsModifyReferenceAndNotesAfterBookingDialogImplementation(
  locationContext: LocationContext,
  locationType: LocationType,
): {
  show: (args: ShowArgs) => Promise<ReferenceNumberAndNotes | undefined>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [showArgs, setShowArgs] = useState<ShowArgs | undefined>(undefined);
  const resolverRef = useRef<
    ((result: ReferenceNumberAndNotes | undefined) => void) | undefined
  >(undefined);

  function show(args: ShowArgs): Promise<ReferenceNumberAndNotes | undefined> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setShowArgs(args);
      setOpenCount((count) => count + 1);
      setOpen(true);
    });
  }

  function settle(result: ReferenceNumberAndNotes | undefined) {
    resolverRef.current?.(result);
    resolverRef.current = undefined;
  }

  const dialogs = showArgs !== undefined && (
    <Dialog
      title="Change Reference and Notes"
      key={`view-shipments-modify-reference-notes-${openCount}`}
      open={open}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          settle(undefined);
        }
      }}
    >
      <ControlPresentationProvider presentation="underlined">
        <ModifyReferenceAndNotesDialogBody
          shipmentId={showArgs.shipmentId}
          referenceNumber={showArgs.referenceNumber}
          amazonReferenceId={showArgs.amazonReferenceId}
          amazonShipmentId={showArgs.amazonShipmentId}
          boothNumber={showArgs.boothNumber}
          notes={showArgs.notes}
          onDone={function (result) {
            settle(result);
            setOpen(false);
          }}
          onCancel={function () {
            settle(undefined);
            setOpen(false);
          }}
          locationContext={locationContext}
          locationType={locationType}
          distributionWarehouseBrand={showArgs.distributionWarehouseBrand}
        />
      </ControlPresentationProvider>
    </Dialog>
  );

  return { show, dialogs };
}
